import type { IndicatorInstance } from "./indicators";

export type MarginMode = "cross" | "isolated";

export const DepositMode = {
  FIXED_USDT: "fixed_usdt",
  BALANCE_PERCENT: "balance_percent",
} as const;
export type DepositMode = (typeof DepositMode)[keyof typeof DepositMode];

// Trailing stop-loss settings (TSL)
export interface TrailingStopLossSettings {
  enabled: boolean;
  trigger_price_step: number; // ratio: 0.7% = 0.007
  order_price_step: number; // ratio: 0.2% = 0.002
}

// Stop-loss settings
export interface StopLossSettings {
  enabled: boolean;
  stop_loss: number; // stop-loss percentage
  placing_timeout: number; // timeout before placing SL order
  timeout_after_execution: number; // timeout after SL execution, 0 = no pause
}

// Fractional TP order
export interface FractionalTakeProfitOrder {
  position_fraction: number; // position fraction to close (ratio: 60% = 0.6)
  tp_rate_ratio: number; // profit ratio for exit (ratio: 0.7% = 0.007)
  exit_on_signal?: boolean; // wait exit on indicator signal (only for last order)
}

// Unified Take Profit settings
export interface TakeProfitSettings {
  orders: FractionalTakeProfitOrder[];
}

// Helper functions for TakeProfitSettings
export const TPSettingsHelpers = {
  /** Check if TP is fractional (more than one order) */
  isFractional: (tp: TakeProfitSettings): boolean => tp.orders.length > 1,

  /** Get simple take profit value (first order's tp_rate_ratio) */
  getSimpleTakeProfit: (tp: TakeProfitSettings): number =>
    tp.orders.length > 0 ? tp.orders[0].tp_rate_ratio : 0,

  /** Create TakeProfitSettings from simple take profit value */
  fromSimpleTP: (takeProfit: number): TakeProfitSettings => ({
    orders: [
      {
        position_fraction: 1, // 100% of position
        tp_rate_ratio: takeProfit,
        exit_on_signal: false,
      },
    ],
  }),
};

export interface TradingGuardSettings {
  enabled: boolean;
  timeout_hours: number; // timeout in hours for soft_stop
  min_move: number; // minimum price movement (%) to allow trading
  max_move: number; // maximum price movement (%) to allow trading
}

export const EntryType = {
  NO_SIGNAL: "NO_SIGNAL", // no-indicator entry
  SIGNAL: "SIGNAL", // signal-based entry
} as const;
export type EntryType = (typeof EntryType)[keyof typeof EntryType];

export const ExitType = {
  NO_SIGNAL: "NO_SIGNAL", // no signal-based exit
  SIGNAL: "SIGNAL", // exit on signals
  SIGNAL_NONNEG: "SIGNAL_NONNEG", // exit on signal with non-negative profit
} as const;
export type ExitType = (typeof ExitType)[keyof typeof ExitType];

export const CapitalAllocationMode = {
  EQUAL: "EQUAL",
  GRID: "GRID",
} as const;
export type CapitalAllocationMode =
  (typeof CapitalAllocationMode)[keyof typeof CapitalAllocationMode];

export const PairPositionMode = {
  HEDGE: "HEDGE",
  NON_HEDGE: "NON_HEDGE",
  GRID: "GRID",
} as const;
export type PairPositionMode = (typeof PairPositionMode)[keyof typeof PairPositionMode];

export const GridExitMode = {
  TP_LIMIT: "TP_LIMIT",
  MARKET: "MARKET",
} as const;
export type GridExitMode = (typeof GridExitMode)[keyof typeof GridExitMode];

// Grid-стратегия: настройки секции TradingProfile.grid (слот с position_mode=GRID).
// Ratio-поля хранятся в UI в процентах (как и остальные настройки) —
// конверсия в доли на границе API (apiTransformation).
export interface GridSettings {
  enabled: boolean;
  anchor_slot: number | null; // слот якорной hedge-пары; null → авто
  levels_count: number; // «Кол-во ордеров»: число ячеек стартовой разметки
  step_ratio: number; // шаг сетки от mid (UI: %)
  exit_mode: GridExitMode; // TP_LIMIT (maker) или MARKET (taker)
  max_live_orders?: number; // удалён из спеки (кап выводится: min(2·levels_count, 100)); поле оставлено optional для чтения старых конфигов
  max_extent_ratio?: number; // удалён из спеки (бюджетный кап — levels_count); поле оставлено optional для чтения старых конфигов
  close_bag_on_negative_pnl: boolean; // market-close мешка при убытке цикла
}

export const AllowedDirection = {
  LONG: "long",
  SHORT: "short",
  BOTH: "both",
} as const;
export type AllowedDirection = (typeof AllowedDirection)[keyof typeof AllowedDirection];

// Параллелизм открытия пар РАЗНЫХ конфигов/таймфреймов в одном цикле.
// Актуально только при per_pair_trading=true с несколькими distinct-конфигами;
// пары одного конфига всегда последовательны независимо от значения.
export const PairEntryMode = {
  PARALLEL: "PARALLEL", // пары разных конфигов открываются независимо (дефолт)
  SEQUENTIAL: "SEQUENTIAL", // следующая пара открывается после заливки entry-ноги предыдущей
} as const;
export type PairEntryMode = (typeof PairEntryMode)[keyof typeof PairEntryMode];

// Cycle-level контейнер: общий для всех хедж-пар (монета задаётся вне BotSettings).
export interface CycleSettings {
  max_hedge_pairs: number; // макс. число одновременно открытых пар
  per_pair_trading: boolean; // true — каждая пара использует свой полный trading-профиль
  pair_entry_mode: PairEntryMode; // PARALLEL/SEQUENTIAL открытие пар разных конфигов
  min_pair_open_interval_minutes: number; // мин. интервал (мин) между открытием пар; 0 — выкл; релевантно при per_pair_trading=false
  capital_allocation_mode: CapitalAllocationMode;
  support_pool_ratio: number; // доля PnL в support-пул (ratio 0..1); остаток — в compound
  margin_mode: MarginMode;
  allowed_direction: AllowedDirection;
  long_leverage: number | null;
  short_leverage: number | null;
}

export interface BaseOrderSettings {
  deposit: number;
  deposit_mode?: DepositMode;
  deposit_percent?: number | null;
}

export interface PairSettings {
  position_mode: PairPositionMode;
  base_order: BaseOrderSettings;
}

export interface PairConfig {
  settings: PairSettings;
  trading: TradingProfile;
}

export const PairCycleTpExitMode = {
  INSTANT: "INSTANT",
  TSL: "TSL",
} as const;
export type PairCycleTpExitMode = (typeof PairCycleTpExitMode)[keyof typeof PairCycleTpExitMode];

export interface PairCycleModuleSettings {
  take_profit: {
    enabled: boolean;
    threshold: number;
    exit_mode: PairCycleTpExitMode;
    callback_ratio: number;
  };
  risk_guard: {
    enabled: boolean;
    max_price_gap: number; // в UI — проценты, на API — ratio (100% = разрыв x2)
    max_deposit_loss: number; // в UI — проценты, на API — ratio (доля живого баланса)
  };
  price_cutter: {
    enabled: boolean;
    min_price_movement_ratio: number; // в UI — проценты, на API — ratio
    mid_center_trigger: boolean; // триггер по мат. центру между ногами
  };
}

// Полный trading-профиль одной хедж-пары.
export interface TradingProfile {
  entry_type: EntryType;
  exit_type: ExitType;
  entry_indicators: IndicatorInstance[]; // entry signal indicators
  exit_indicators: IndicatorInstance[]; // exit signal indicators
  take_profit: {
    enabled: boolean;
    orders: FractionalTakeProfitOrder[];
  };
  stop_loss: StopLossSettings;
  trailing_stop_loss: TrailingStopLossSettings;
  trading_guard: TradingGuardSettings;
  pair_cycle: PairCycleModuleSettings;
  grid: GridSettings; // актуально при position_mode=GRID слота
}

// Версия схемы bot_settings; должна соответствовать
// `bot_settings.settings.CURRENT_SETTINGS_VERSION` на стороне сервера.
export const BOT_SETTINGS_VERSION = 13;

export interface BotSettings {
  settings_version: number;
  cycle: CycleSettings;
  // Slot-indexed configs 0..cycle.max_hedge_pairs-1 с полными settings и trading.
  pair_configs: PairConfig[];
}
